import React, { useEffect, useMemo, useState } from "react";
import { AnchoredModal } from "./AnchoredModal";
import { Button } from "./Button";

export interface GlobalMatchesExportRow {
  eventId: number;
  eventName: string;
  locationName: string;
  dayKey: string;
  timeLabel: string;
  player1Name: string;
  player2Name: string;
  stageName: string;
  scoreLabel: string;
}

interface GlobalMatchesExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUserLabel: string;
  dateBounds: {
    min: string;
    max: string;
  };
  rows: GlobalMatchesExportRow[];
  onExportExcel: (range: { startDate: string; endDate: string }) => Promise<void> | void;
  onExportPdf: (range: { startDate: string; endDate: string }) => Promise<void> | void;
}

export const GlobalMatchesExportModal: React.FC<GlobalMatchesExportModalProps> = ({
  isOpen,
  onClose,
  selectedUserLabel,
  dateBounds,
  rows,
  onExportExcel,
  onExportPdf,
}) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStartDate(dateBounds.min);
    setEndDate(dateBounds.max);
  }, [dateBounds.max, dateBounds.min, isOpen]);

  const filteredRows = useMemo(() => {
    if (!startDate || !endDate) return rows;
    return rows.filter((row) => row.dayKey >= startDate && row.dayKey <= endDate);
  }, [endDate, rows, startDate]);

  const groupedRows = useMemo(() => {
    const eventMap = new Map<
      number,
      {
        eventName: string;
        locationName: string;
        dates: Map<string, GlobalMatchesExportRow[]>;
      }
    >();

    filteredRows.forEach((row) => {
      const eventGroup = eventMap.get(row.eventId) ?? {
        eventName: row.eventName,
        locationName: row.locationName,
        dates: new Map<string, GlobalMatchesExportRow[]>(),
      };
      eventGroup.dates.set(row.dayKey, [...(eventGroup.dates.get(row.dayKey) ?? []), row]);
      eventMap.set(row.eventId, eventGroup);
    });

    return Array.from(eventMap.entries())
      .map(([eventId, eventGroup]) => ({
        eventId,
        eventName: eventGroup.eventName,
        locationName: eventGroup.locationName,
        dates: Array.from(eventGroup.dates.entries())
          .sort(([dayA], [dayB]) => dayA.localeCompare(dayB))
          .map(([dayKey, dateRows]) => ({
            dayKey,
            rows: dateRows.sort((a, b) => a.timeLabel.localeCompare(b.timeLabel)),
          })),
      }))
      .sort((a, b) => a.eventName.localeCompare(b.eventName));
  }, [filteredRows]);

  const handleExportExcel = async () => {
    if (!startDate || !endDate || !groupedRows.length || isExportingExcel) return;
    setIsExportingExcel(true);
    try {
      await onExportExcel({ startDate, endDate });
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    if (!startDate || !endDate || !groupedRows.length || isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      await onExportPdf({ startDate, endDate });
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnchoredModal
      title="Esporta calendario"
      onClose={onClose}
      maxWidthClassName="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
          <div className="flex gap-2">
            <Button variant="success" isOutlined={true} onClick={handleExportExcel} disabled={!groupedRows.length || isExportingExcel}>
              {isExportingExcel ? "Esportazione Excel..." : "Export Excel"}
            </Button>
            <Button variant="destructive" isOutlined={true} onClick={handleExportPdf} disabled={!groupedRows.length || isExportingPdf}>
              {isExportingPdf ? "Esportazione PDF..." : "Export PDF"}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Filtro utente: <span className="font-medium text-gray-900">{selectedUserLabel}</span>
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="block text-sm font-medium text-gray-700">Da data</span>
            <input
              type="date"
              value={startDate}
              min={dateBounds.min || undefined}
              max={endDate || dateBounds.max || undefined}
              onChange={(event) => setStartDate(event.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900"
            />
          </label>

          <label className="space-y-1">
            <span className="block text-sm font-medium text-gray-700">A data</span>
            <input
              type="date"
              value={endDate}
              min={startDate || dateBounds.min || undefined}
              max={dateBounds.max || undefined}
              onChange={(event) => setEndDate(event.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900"
            />
          </label>
        </div>

        <div className="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
          {groupedRows.length
            ? `${filteredRows.length} match inclusi nell'esportazione.`
            : "Nessun match disponibile nell'intervallo selezionato."}
        </div>
      </div>
    </AnchoredModal>
  );
};
